<template>
  <div class="email-address-page">
    <!-- Header Section -->
    <AppHeader 
      header-text="Drivers in your state can save up to $600/year"
      button-text="FREE CALL"
    />

    <!-- Progress Bar -->
    <div class="progress-container">
      <div class="progress-bar">
        <div class="progress-fill"></div>
      </div>
    </div>

    <!-- Navigation -->
    <div class="navigation">
      <div class="container">
        <button class="back-btn" @click="goBack">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Previous Question
        </button>
      </div>
    </div>

    <!-- Email Address Section -->
    <section class="email-address-section">
      <div class="container">
        <div class="email-content">
          <PageTitle title="Email Address" />
          
          <div class="email-form">
            <!-- Email Input -->
            <div class="input-group">
              <input
                type="email"
                v-model="emailAddress"
                placeholder="Email Address"
                class="email-input"
                :class="{ 'error': showEmailError }"
                @input="clearEmailError"
              />
              <div v-if="showEmailError" class="error-message">{{ emailErrorMessage }}</div>
            </div>

            <!-- Continue Button -->
            <div class="continue-section">
              <button 
                class="continue-btn"
                @click="continueToNext"
              >
                CONTINUE
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer Links -->
    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import PageTitle from '@/components/PageTitle.vue'
import { submitDataAndLog } from '@/utils/localStorage'

export default {
  name: 'EmailAddressPage',
  components: {
    AppHeader,
    AppFooter,
    PageTitle
  },
  data() {
    return {
      userQuoteData: null,
      emailAddress: '',
      showEmailError: false,
      emailErrorMessage: 'Required'
    }
  },
  mounted() {
    this.loadUserData()
  },
  methods: {
    loadUserData() {
      const storedData = localStorage.getItem('quoteData')
      if (storedData) {
        try {
          this.userQuoteData = JSON.parse(storedData)
          // 检查是否有房屋所有权信息
          if (!this.userQuoteData.home_ownership) {
            // 如果没有房屋所有权信息，跳转回home-ownership页面
            this.$router.push('/home-ownership')
            return
          }
          // 加载之前输入的邮箱（如果有的话）
          if (this.userQuoteData.email) {
            this.emailAddress = this.userQuoteData.email
          }
        } catch (error) {
          console.error('Error parsing stored data:', error)
          this.$router.push('/')
        }
      } else {
        // 如果没有数据，跳转回首页
        this.$router.push('/')
      }
    },
    
    saveUserData() {
      localStorage.setItem('quoteData', JSON.stringify(this.userQuoteData))
    },
    
    clearEmailError() {
      if (this.emailAddress && this.emailAddress.trim().length > 0) {
        this.showEmailError = false
      }
    },
    
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },
    
    async continueToNext() {
      console.log('CONTINUE button clicked in Email Address page')
      
      // 验证邮箱
      if (!this.emailAddress || this.emailAddress.trim().length === 0) {
        this.showEmailError = true
        this.emailErrorMessage = 'Required'
        return
      }
      
      if (!this.isValidEmail(this.emailAddress.trim())) {
        this.showEmailError = true
        this.emailErrorMessage = 'Please enter a valid email address'
        return
      }
      
      this.showEmailError = false
      
      // 更新用户数据
      this.userQuoteData.email = this.emailAddress.trim()
      this.saveUserData()
      
      console.log('Email saved:', this.emailAddress.trim())
      
      // 提交数据并记录页面提交事件
      await submitDataAndLog('emailaddress')
      // 跳转到Get Quotes页面
      this.$router.push('/get-quotes')
    },
    
    goBack() {
      // 根据来源页面返回
      if (this.userQuoteData && this.userQuoteData.home_ownership === 'own') {
        // 如果是从Bundle Home页面来的
        this.$router.push('/bundle-home')
      } else {
        // 如果是直接从Home Ownership页面来的
        this.$router.push('/home-ownership')
      }
    }
  }
}
</script>

<style scoped>
/* Page Layout */
.email-address-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}


/* Navigation */
.navigation {
  padding: 1rem 0;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #999;
  font-size: 0.9rem;
  cursor: pointer;
  transition: color 0.3s ease;
}

.back-btn:hover {
  color: #666;
}

/* Email Address Section */
.email-address-section {
  padding: 2rem 0 4rem;
  background: #fff;
  flex: 1;
}

.email-content {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
  padding: 0 2rem;
}

.email-form {
  margin-top: 2rem;
  text-align: left;
}

.input-group {
  position: relative;
  margin-bottom: 2rem;
}

.email-input {
  width: 100%;
  padding: 1rem 0;
  border: none;
  border-bottom: 2px solid #e9ecef;
  border-radius: 0;
  background: transparent;
  font-size: 1rem;
  color: #333;
  transition: border-color 0.3s ease;
  outline: none;
}

.email-input:focus {
  border-bottom-color: #007bff;
}

.email-input.error {
  border-bottom-color: #dc3545;
}

.email-input::placeholder {
  color: #999;
  font-weight: 400;
}

.error-message {
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 0.5rem;
  text-align: left;
}

/* Continue Section */
.continue-section {
  margin-top: 3rem;
  padding-top: 2rem;
  text-align: center;
}

.continue-btn {
  padding: 1rem 3rem;
  border: 2px solid #007bff;
  border-radius: 8px;
  background: #fff;
  color: #007bff;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 200px;
}

.continue-btn:hover {
  background: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,123,255,0.15);
}

/* Responsive Design */
@media (max-width: 768px) {
  .email-content {
    padding: 0 1rem;
  }

  .continue-btn {
    width: 100%;
    max-width: 300px;
  }
}

@media (max-width: 480px) {
  .email-content {
    padding: 0 0.5rem;
  }

  .email-input {
    padding: 0.8rem 0;
    font-size: 0.9rem;
  }

  .continue-btn {
    padding: 0.8rem 2rem;
    font-size: 1rem;
  }
}
</style>
